import { ReactNode } from "react";
import clsx from "clsx";
import type { MeetingType } from "skedify-types";

import type { AppointmentSummaryProps } from "./AppointmentSummary";

import { LocationIcon } from "~/components/Icons/LocationIcon";
import { OfficeIcon } from "~/components/Icons/OfficeIcon";
import { PhoneIcon } from "~/components/Icons/PhoneIcon";
import { VideoIcon } from "~/components/Icons/VideoIcon";
import { Text } from "~/components/Text";

interface MeetingTypeSummaryProps {
  employeeName: string | null;
  meetingType: MeetingType;
  locationAddress: string | null;
  office: AppointmentSummaryProps["office"];
  className?: string;
}

const components: Record<MeetingType, (props: MeetingTypeSummaryProps) => ReactNode> = {
  office: OfficeContent,
  on_location: OnLocationContent,
  phone: PhoneContent,
  video: VideoContent,
};

export function MeetingTypeSummary(props: MeetingTypeSummaryProps) {
  const { meetingType } = props;
  const Content = components[meetingType];

  return <Content {...props} data-variant={meetingType} />;
}

function OfficeContent({ className, office }: MeetingTypeSummaryProps) {
  return (
    <MeetingTypeContent
      className={className}
      icon={<OfficeIcon outline />}
      label={
        <Text i18nKey={office?.isVirtual ? "summary.contact_center" : "summary.office_label"} />
      }
    >
      {office?.title ? (
        <>
          <div>{office.title}</div>
          {!office.isVirtual && <div>{office.address}</div>}
        </>
      ) : (
        <Text i18nKey="summary.office" />
      )}
    </MeetingTypeContent>
  );
}

function OnLocationContent({ className, locationAddress }: MeetingTypeSummaryProps) {
  return (
    <MeetingTypeContent
      className={className}
      icon={<LocationIcon outline />}
      label={<Text i18nKey="summary.location" />}
    >
      {locationAddress}
    </MeetingTypeContent>
  );
}

function PhoneContent({ className, employeeName }: MeetingTypeSummaryProps) {
  return (
    <MeetingTypeContent
      className={className}
      icon={<PhoneIcon outline />}
      label={<Text i18nKey="steps.MeetingType.meeting_types.phone" />}
    >
      {employeeName ? (
        <Text i18nKey="summary.phone.with_employee" values={{ name: employeeName }} />
      ) : (
        <Text i18nKey="summary.phone.without_employee" />
      )}
    </MeetingTypeContent>
  );
}

function VideoContent({ className }: MeetingTypeSummaryProps) {
  return (
    <MeetingTypeContent
      className={className}
      icon={<VideoIcon outline />}
      label={<Text i18nKey="steps.MeetingType.meeting_types.video" />}
    >
      {/* TODO: agent cf. phone? */}
      <Text i18nKey="summary.video" />
    </MeetingTypeContent>
  );
}

interface MeetingTypeContentProps {
  label: ReactNode;
  icon: ReactNode;
  children: ReactNode;
  className?: string;
}

function MeetingTypeContent({ children, className, icon, label }: MeetingTypeContentProps) {
  return (
    <div className={clsx("grid gap-3xs", className)} data-component="MeetingTypeSummary">
      <span className="text-sm font-semibold">{label}</span>

      <div className="flex items-start gap-xs">
        <div className="flex h-[20px] w-[20px] justify-center text-brand">{icon}</div>

        <div
          className="text-sm font-normal text-gray-500"
          data-component="MeetingTypeSummaryContentChildren"
        >
          {children}
        </div>
      </div>
    </div>
  );
}
